// The published workflow definition WEB-0 drives end-to-end (seeded on the stack).
export const REFERRAL_INTAKE_API_NAME = 'referral_intake';
// The second workflow type (OLIO-2593): integration-triggered portal pulls that spawn
// or update referral-intake runs. Shares the show page; has its own list + URL space.
export const PORTAL_AUTOMATION_API_NAME = 'portal_automation';

export const INBOUND_REFERRALS_ROUTES = {
  BASE: '/inbound-referrals',
  // Create + edit are modals now (ReferralModal), not routed pages.
};

export const PORTAL_AUTOMATION_ROUTES = {
  BASE: '/portal-automation',
};

// The list/show URL space a run belongs to, by its definition apiName — so links and
// back navigation stay type-correct as more workflow types arrive.
export function workflowBasePath(apiName: string): string {
  return apiName === PORTAL_AUTOMATION_API_NAME ? PORTAL_AUTOMATION_ROUTES.BASE : INBOUND_REFERRALS_ROUTES.BASE;
}

// The demographic value names this UI reads/writes — snake_case, sent verbatim. They are
// the seed's value names (app/seeds/seed_data/workflows.rb), not API field names, so
// OliveBranch leaves them alone. (The packet/attachment value names live in actionConfig's
// graph vocabulary — this UI uploads `attachments` but never reads attachment_ids /
// attachments_processed directly.)
export const VALUE_NAMES = {
  PATIENT_FIRST_NAME: 'patient_first_name',
  PATIENT_LAST_NAME: 'patient_last_name',
  PATIENT_GENDER: 'patient_gender',
  PATIENT_DOB: 'patient_dob',
  PLAN_TYPE: 'plan_type',
  REFERRAL_SOURCE: 'referral_source',
  // Benefits-eligibility inputs (Stedi 270) — the payer, the rendering provider, and the
  // member id. Names match olio-api's VALUE_CATALOG (seed_data/workflows.rb); `payer_id` is
  // produced by PayerSearch (not user-editable), so it isn't a form field.
  MEMBER_ID: 'member_id',
  PROVIDER_NPI: 'provider_npi',
  PROVIDER_ORGANIZATION_NAME: 'provider_organization_name',
} as const;

export type ReferralFieldType = 'text' | 'date' | 'select';

export interface ReferralFieldDef {
  name: string;
  label: string;
  type: ReferralFieldType;
  optional?: boolean;
}

// Patient Demographics — extract_attributes produces these and a person can also seed/edit
// them. `label` is the entry-form label; rendered values prefer the API's value label. Sex is
// a select rendered with the shared GENDERS options (same as /patients/new).
export const DEMOGRAPHIC_FIELDS: ReferralFieldDef[] = [
  { name: VALUE_NAMES.PATIENT_FIRST_NAME, label: 'First name', type: 'text' },
  { name: VALUE_NAMES.PATIENT_LAST_NAME, label: 'Last name', type: 'text' },
  { name: VALUE_NAMES.PATIENT_GENDER, label: 'Sex', type: 'select' },
  { name: VALUE_NAMES.PATIENT_DOB, label: 'Date of birth', type: 'date' },
];

// Insurance Information — plan_type (the payer) is AI-extractable and surfaces as the list's
// "Plan Type" column. member_id / provider_npi / provider_organization_name are the
// benefits-eligibility check's Stedi 270 inputs; they have no in-graph producer yet, so a person
// supplies them here (editing them re-runs the check). payer_id is produced by PayerSearch, not
// entered.
export const INSURANCE_FIELDS: ReferralFieldDef[] = [
  { name: VALUE_NAMES.PLAN_TYPE, label: 'Payer Name', type: 'text', optional: true },
  { name: VALUE_NAMES.MEMBER_ID, label: 'Member ID', type: 'text', optional: true },
  { name: VALUE_NAMES.PROVIDER_NPI, label: 'Provider NPI', type: 'text', optional: true },
  { name: VALUE_NAMES.PROVIDER_ORGANIZATION_NAME, label: 'Provider Organization', type: 'text', optional: true },
];
